// src/pages/AdminBlogCategoriesPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiPlus, FiTrash2, FiSave, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { BlogService, type CreateCategoryDto, type UpdateCategoryDto } from '../services/blog.service';
import type { BlogCategory } from '../types/Blog';
import styles from './AdminBlogCategoriesPage.module.css';

type FormMode = 'create' | 'edit';

const generateSlug = (value: string) => {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export default function AdminBlogCategoriesPage() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<FormMode>('create');
  const [saving, setSaving] = useState(false);

  const [editing, setEditing] = useState<BlogCategory | null>(null);

  const [form, setForm] = useState<CreateCategoryDto>({
    name: '',
    slug: '',
    is_active: true,
    parentId: null
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await BlogService.getAdminCategories();
      setCategories(Array.isArray(res) ? res : []);
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setMode('create');
    setForm({ name: '', slug: '', is_active: true, parentId: null });
  };

  const openCreate = () => {
    setMode('create');
    setEditing(null);
    setForm({ name: '', slug: '', is_active: true, parentId: null });
    setOpen(true);
  };

  const openEdit = (cat: BlogCategory) => {
    setMode('edit');
    setEditing(cat);
    setForm({
      name: cat.name || '',
      slug: cat.slug || '',
      is_active: cat.is_active ?? true,
      parentId: null
    });
    setOpen(true);
  };

  const activeCategories = useMemo(() => categories.filter(c => c.is_active !== false), [categories]);

  const rows = useMemo(() => {
    return categories.map(c => {
      const slug = (c.slug || '').trim();
      const statusLabel = c.is_active !== false ? t('common.active') || 'Activa' : t('common.inactive') || 'Inactiva';
      const statusClass = c.is_active !== false ? styles.statusActive : styles.statusInactive;

      return {
        id: c.id,
        name: c.name?.trim() || '-',
        slug: slug || '-',
        canMutate: Boolean(slug),
        statusLabel,
        statusClass,
        raw: c
      };
    });
  }, [categories, t]);

  const handleDelete = async (cat: BlogCategory) => {
    const slug = (cat.slug || '').trim();
    if (!slug) return;

    const ok = window.confirm(t('admin.confirm_delete_category') || '¿Eliminar esta categoría?');
    if (!ok) return;

    try {
      await BlogService.deleteCategory(slug);
    } catch {
      alert(t('admin.category_delete_error') || 'Error al eliminar la categoría');
    } finally {
      load();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name?.trim();
    const slug = form.slug?.trim();

    if (!name || !slug) return;

    setSaving(true);
    try {
      if (mode === 'create') {
        await BlogService.createCategory({
          name,
          slug,
          is_active: form.is_active ?? true,
          parentId: form.parentId ?? null
        });
      } else {
        const currentSlug = (editing?.slug || '').trim();
        if (!currentSlug) throw new Error('Missing slug');

        const payload: UpdateCategoryDto = {
          name,
          is_active: form.is_active ?? true,
          parentId: form.parentId ?? null
        };

        await BlogService.updateCategory(currentSlug, payload);
      }

      await load();
      closeModal();
    } catch {
      alert(t('admin.category_save_error') || 'Error al guardar la categoría');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <Link to="/admin/blogs" className={styles.backButton} aria-label={t('common.back') || 'Volver'}>
            <FiArrowLeft />
          </Link>

          <div className={styles.headerText}>
            <h1 className={styles.title}>{t('admin.categories') || 'Categorías'}</h1>
            <p className={styles.subtitle}>{t('admin.categories_subtitle') || 'Crea, edita y elimina categorías del blog.'}</p>
          </div>
        </div>

        <button type="button" className={styles.primaryAction} onClick={openCreate}>
          <FiPlus className={styles.actionIcon} />
          {t('admin.new_category') || 'Nueva Categoría'}
        </button>
      </header>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('admin.categories') || 'Categorías'}</div>
          <div className={styles.countBadge}>{loading ? '—' : String(categories.length)}</div>
        </div>

        <div className={styles.cardBody}>
          {loading ? (
            <div className={styles.loading}>{t('common.loading') || 'Cargando...'}</div>
          ) : rows.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>{t('admin.no_categories') || 'No hay categorías'}</div>
              <div className={styles.emptyHint}>{t('admin.create_first_category_hint') || 'Crea una categoría para organizar tus publicaciones.'}</div>
              <button type="button" className={styles.secondaryAction} onClick={openCreate}>
                <FiPlus className={styles.actionIcon} />
                {t('admin.new_category') || 'Nueva Categoría'}
              </button>
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thName}>{t('admin.table_name') || 'Nombre'}</th>
                    <th className={styles.thSlug}>{t('admin.slug') || 'Slug'}</th>
                    <th className={styles.thStatus}>{t('admin.table_status') || 'Estado'}</th>
                    <th className={styles.thActions}>{t('admin.table_actions') || 'Acciones'}</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map(r => (
                    <tr key={String(r.id)} className={styles.tr}>
                      <td className={styles.tdName}>
                        <div className={styles.nameCell}>
                          <div className={styles.name}>{r.name}</div>
                        </div>
                      </td>

                      <td className={styles.tdSlug}>
                        <span className={styles.slugPill}>{r.slug}</span>
                      </td>

                      <td className={styles.tdStatus}>
                        <span className={`${styles.statusChip} ${r.statusClass}`}>{r.statusLabel}</span>
                      </td>

                      <td className={styles.tdActions}>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className={styles.iconButton}
                            onClick={() => openEdit(r.raw)}
                            aria-label={t('common.edit') || 'Editar'}
                            disabled={!r.canMutate}
                            title={!r.canMutate ? 'No editable' : undefined}
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            type="button"
                            className={`${styles.iconButton} ${styles.danger}`}
                            onClick={() => handleDelete(r.raw)}
                            aria-label={t('common.delete') || 'Eliminar'}
                            disabled={!r.canMutate}
                            title={!r.canMutate ? 'No eliminable' : undefined}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {activeCategories.length === 0 ? (
                <div className={styles.note}>
                  {t('admin.no_active_categories_note') || 'No hay categorías activas. Activa al menos una para poder asignarla a publicaciones.'}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </section>

      {open ? (
        <div className={styles.overlay} role="dialog" aria-modal="true" onClick={closeModal}>
          <div className={styles.sheet} onClick={e => e.stopPropagation()}>
            <div className={styles.sheetHeader}>
              <div className={styles.sheetTitle}>
                {mode === 'create' ? t('admin.new_category') || 'Nueva Categoría' : t('admin.edit_category') || 'Editar Categoría'}
              </div>

              <button type="button" className={styles.closeBtn} onClick={closeModal} aria-label={t('common.close') || 'Cerrar'}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>{t('admin.category_name') || 'Nombre'}</label>
                <input
                  className={styles.input}
                  value={form.name}
                  onChange={e => {
                    const name = e.target.value;
                    setForm(prev => ({
                      ...prev,
                      name,
                      slug: mode === 'create' ? generateSlug(name) : prev.slug
                    }));
                  }}
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>{t('admin.slug') || 'Slug'}</label>
                <input
                  className={styles.input}
                  value={form.slug}
                  onChange={e => setForm(prev => ({ ...prev, slug: e.target.value }))}
                  required
                  disabled={mode === 'edit'}
                />
                {mode === 'edit' ? (
                  <div className={styles.helper}>{t('admin.slug_locked_hint') || 'El slug no se puede cambiar al editar.'}</div>
                ) : null}
              </div>

              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.is_active ?? true}
                  onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                <span>{t('common.active') || 'Activa'}</span>
              </label>

              <div className={styles.footer}>
                <button type="button" className={styles.secondaryAction} onClick={closeModal} disabled={saving}>
                  {t('common.cancel') || 'Cancelar'}
                </button>
                <button type="submit" className={styles.primaryActionInline} disabled={saving}>
                  <FiSave className={styles.actionIcon} />
                  {saving ? t('common.saving') || 'Guardando...' : t('common.save') || 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
